import { useEffect, useState } from "react";
import { Flame, CalendarCheck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState } from "@/components/States";
import QuizPlayer from "@/components/QuizPlayer";
import QuizResult from "@/components/QuizResult";
import { api, formatError } from "@/lib/api";

export default function Daily() {
  const [status, setStatus] = useState(null);
  const [session, setSession] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/daily").then((r) => setStatus(r.data)).catch((e) => setErr(formatError(e)));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true); setErr("");
    try { setSession((await api.post("/daily/start")).data); } catch (e) { setErr(formatError(e)); }
    setBusy(false);
  };
  const finished = (a) => { setAttempt(a); setSession(null); load(); };

  if (!status) return err ? <ErrorState message={err} onRetry={load} /> : <Loading />;
  if (attempt) return <QuizResult attempt={attempt} />;
  if (session) return <QuizPlayer session={session} onFinish={finished} />;

  return (
    <div data-testid="daily-page">
      <PageHeader eyebrow={new Date(status.date).toDateString()} title="Daily quiz" text="A short set of expert-approved questions from your preferred subjects. One per day — keep your streak going." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl bg-band p-8 text-white lg:col-span-2">
          {status.completed ? (
            <div data-testid="daily-completed">
              <CheckCircle2 className="h-8 w-8 text-[var(--brand-cyan)]" />
              <h2 className="mt-4 text-2xl font-extrabold">Today's quiz is complete</h2>
              <p className="mt-2 text-slate-300">You scored <b data-testid="daily-today-score">{status.attempt.score}/{status.attempt.total}</b> ({status.attempt.percentage}%). Come back tomorrow for a new set.</p>
              <Button asChild variant="outline" className="mt-6 rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"><Link data-testid="daily-review-link" to={`/history/${status.attempt.id}`}>Review answers</Link></Button>
            </div>
          ) : (
            <div>
              <CalendarCheck className="h-8 w-8 text-[var(--brand-cyan)]" />
              <h2 className="mt-4 text-2xl font-extrabold">Ready for today's challenge?</h2>
              <p className="mt-2 text-slate-300">Questions are drawn from the approved IDDCR question bank — no waiting for AI.</p>
              {err && <p data-testid="daily-error" className="mt-4 rounded-md bg-red-500/20 px-3 py-2 text-sm">{err}</p>}
              <Button data-testid="daily-start-btn" disabled={busy} onClick={start} className="mt-6 rounded-full bg-aqua-solid px-6 hover:bg-aqua-solid-hover">Start daily quiz</Button>
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-line bg-surface p-8">
          <Flame className="h-8 w-8 text-orange-500" />
          <p data-testid="daily-streak" className="mt-4 font-display text-5xl font-extrabold text-ink">{status.streak}</p>
          <p className="text-sm text-ink-3">day learning streak</p>
        </div>
      </div>
      <h3 className="mb-4 mt-10 font-bold text-ink">Recent daily quizzes</h3>
      {status.history.length === 0 ? <p data-testid="daily-history-empty" className="text-sm text-ink-3">No daily quizzes yet.</p> : (
        <div className="flex flex-wrap gap-3" data-testid="daily-history">
          {status.history.map((h) => (
            <Link key={h.id} to={`/history/${h.id}`} className="rounded-xl border border-line bg-surface px-4 py-3 text-sm hover:border-aqua">
              <p className="font-mono text-xs text-ink-3">{h.date}</p><p className="font-semibold text-ink">{h.score}/{h.total}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
